import type { OcpiLocation } from "../types/ocpi";
import { locationHasAc, locationHasDc, formatPrice } from "../utils/format";
import { TARIFFS, TARIFF_LABELS, type TariffKey } from "../config/tariffs";
import { useI18n } from "../i18n/LanguageContext";
import { BRAND } from "../config/brand";

const TIER_COLOR: Record<TariffKey, string> = {
  AC: BRAND.ac,
  DC: BRAND.dcFast,
};

export function TariffBox({ location }: { location: OcpiLocation }) {
  const { t, lang } = useI18n();
  const rows: TariffKey[] = [];
  if (locationHasAc(location)) rows.push("AC");
  if (locationHasDc(location)) rows.push("DC");
  if (rows.length === 0) return null;

  return (
    <section className="rounded-2xl border border-ink-100 bg-white p-4 shadow-soft">
      <header className="mb-3 flex items-center justify-between">
        <h3 className="text-[13px] font-semibold tracking-wide text-ink-700 uppercase">
          {t("tariffs")}
        </h3>
        <span className="text-[11px] text-ink-400">{t("vatIncluded")}</span>
      </header>
      <ul className="divide-y divide-ink-100">
        {rows.map((key) => {
          const tariff = TARIFFS[key];
          const color = TIER_COLOR[key];
          return (
            <li key={key} className="flex items-center justify-between gap-3 py-2.5">
              <div className="flex min-w-0 items-center gap-3">
                <span
                  className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-white shadow-soft"
                  style={{ background: color }}
                  aria-hidden
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />
                  </svg>
                </span>
                <span className="truncate text-[13px] font-medium text-ink-900">
                  {TARIFF_LABELS[key][lang]}
                </span>
              </div>
              <div className="flex items-baseline gap-1 whitespace-nowrap tabular-nums">
                <span className="text-[17px] font-semibold text-ink-900">
                  {formatPrice(tariff.price, tariff.currency, lang)}
                </span>
                <span className="text-[11px] text-ink-400">{t("perKwh")}</span>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
